import { IExceptionList } from "@/@types/exception-list";
import { ColumnDef } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { maskNcm } from "../../utils/utils";
import { DataTableColumnHeader } from "./data-table-column-header";
import { DataTableRowActions } from "./data-table-row-actions";
import { Badge } from "@chakra-ui/react";


export const getColumns = (
  type: "NCM" | "PRODUCT"
): ColumnDef<IExceptionList>[] => [
  {
    accessorKey: "state",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-gray-700">
        {row.original.state?.toUpperCase() === "ALL"
          ? "TODOS"
          : row.original.state}
      </span>
    ),
  },

  {
    accessorKey: "rule_name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nome da Regra" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-gray-700">
        {row.original.rule_name}
      </span>
    ),
  },
  {
    id: "code",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={type === "NCM" ? "NCM" : "SKU"}
      />
    ),
    cell: ({ row }) => {
      const { ncm, sku } = row.original;
      const value = type === "NCM" ? maskNcm(ncm || "") : sku;
      return (
        <span className="text-sm font-semibold text-gray-700">
          {value === "all" ? "TODOS" : value}
        </span>
      );
    },
  },
  {
    accessorKey: "exception_type",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tributo" />
    ),
    cell: ({ row }) => (
      <Badge
        variant="outline"
        className="text-sm font-semibold text-[#E89410] bg-orange-100 border border-orange-300 px-3 py-1 rounded-full"
      >
        {row.original.exception_type}
      </Badge>
    ),
  },
  {
    accessorKey: "reason",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Motivo" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-gray-700">
        {row.original.reason || " - "}
      </span>
    ),
  },
  {
    accessorKey: "exception_rate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Aliquota" />
    ),
    cell: ({ row }) => {
      const rate = row.original.exception_rate;
      if (rate === null || rate === undefined) return <span className="text-sm font-semibold text-gray-700">-</span>;
      return (
        <span className="text-sm font-semibold text-gray-700">
          {`${rate.toString().replace(".", ",")}%`}
        </span>
      );
    },
  },
  {
    accessorKey: "calculation_basis",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Base de Cálculo" />
    ),
    cell: ({ row }) => {
      const basis = row.original.calculation_basis;
      if (basis === null || basis === undefined) return <span className="text-sm font-semibold text-gray-700">-</span>;
      return (
        <span className="text-sm font-semibold text-gray-700">
          {`${basis.toString().replace(".", ",")}%`}
        </span>
      );
    },
  },
  {
    accessorKey: "cfop",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CFOP" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-gray-700">
        {row.original.cfop || "-"}
      </span>
    ),
  },
  {
    accessorKey: "cst",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CST" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-gray-700">
        {row.original.cst || "-"}
      </span>
    ),
  },
  {
    accessorKey: "classification_tax",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Classificação Tributária" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-gray-700">
        {row.original.classification_tax || " - "}
      </span>
    ),
  },
  {
    accessorKey: "cest",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CEST" />
    ),
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-gray-700">
        {row.original.cest || " - "}
      </span>
    ),
  },
  {
    accessorKey: "initial_term",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data Inicial da Exceção" />
    ),
    cell: ({ row }) => {
      const dateString = row.getValue("initial_term") as string;
      if (!dateString) return <span className="text-sm font-semibold text-gray-700">-</span>;
      try {
        const parsedDate = parseISO(dateString);
        return (
          <span className="text-sm font-semibold text-gray-700">
            {format(parsedDate, "dd/MM/yyyy", { locale: ptBR })}
          </span>
        );
      } catch {
        return <span className="text-sm font-semibold text-gray-700">Data Inválida</span>;
      }
    },
  },
  {
    accessorKey: "final_term",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data Final da Exceção" />
    ),
    cell: ({ row }) => {
      const dateString = row.getValue("final_term") as string;
      if (!dateString) return <span className="text-sm font-semibold text-gray-700">-</span>;
      try {
        const parsedDate = parseISO(dateString);
        return (
          <span className="text-sm font-semibold text-gray-700">
            {format(parsedDate, "dd/MM/yyyy", { locale: ptBR })}
          </span>
        );
      } catch {
        return <span className="text-sm font-semibold text-gray-700">Data Inválida</span>;
      }
    },
  },
  {
    id: "status",
    header: "",
    cell: ({ row }) => {
      const now = new Date();
      const initial = row.original.initial_term
        ? new Date(row.original.initial_term)
        : null;
      const final = row.original.final_term
        ? new Date(row.original.final_term)
        : null;

      if (initial && now < initial) {
        return (
          <Badge className="bg-orange-100 text-orange-800 border border-orange-300 px-3 py-1 rounded-full text-xs font-semibold">
            Programado
          </Badge>
        );
      }

      if (final && now > final) {
        return (
          <Badge className="bg-red-100 text-red-800 border border-red-300 px-3 py-1 rounded-full text-xs font-semibold">
            Expirado
          </Badge>
        );
      }

      if (initial && (!final || now <= final) && now >= initial) {
        return (
          <Badge className="bg-green-100 text-green-800 border border-green-300 px-3 py-1 rounded-full text-xs font-semibold">
            Em Vigor
          </Badge>
        );
      }

      return null;
    },
  },

  {
    id: "actions",
    header: "Ações",
    cell: ({ row }) => <DataTableRowActions<IExceptionList> row={row} />,
  },
];
